import { z } from 'zod'
import { DateStringSchema } from './booking'
import { addDays } from './membership'

// The palolento (fire-patrol) calendar: who flies and observes on which day,
// under a dispatcher chosen for the week. The flights themselves are
// ordinary bookings (flight.bookings.is_fire_patrol), so they show in the
// main calendar too. Tables: flight.fire_patrol_crew, _weeks and _shifts.

// A day's roles: the pilot in command (lentäjä) and the observer / mission
// crew (tähystäjä). A sign-up with no role is "not known yet", or an extra.
export const FIRE_PATROL_ROLES = ['pic', 'mc'] as const
export type FirePatrolRole = (typeof FIRE_PATROL_ROLES)[number]

const RoleSchema = z.enum(FIRE_PATROL_ROLES)
const NoteSchema = z
  .string()
  .trim()
  .max(500)
  .nullish()
  .transform((v) => v || null)

// The longest stretch one calendar request may cover (a season's month view
// with its padding days).
export const FIRE_PATROL_MAX_RANGE_DAYS = 62

export const FirePatrolRangeSchema = z.object({ from: DateStringSchema, to: DateStringSchema })

// Signing up for a day. `userId` is for a dispatcher signing someone else up.
export const FirePatrolSignUpSchema = z.object({
  shiftDate: DateStringSchema,
  role: RoleSchema.nullish().transform((v) => v ?? null),
  note: NoteSchema,
  userId: z.string().trim().min(1).max(20).optional(),
})
export type FirePatrolSignUpInput = z.infer<typeof FirePatrolSignUpSchema>

// Editing a shift. `locked` is the dispatcher's "chosen": true locks the
// person into the role, false releases them.
export const FirePatrolShiftPatchSchema = z
  .object({
    role: RoleSchema.nullable().optional(),
    note: NoteSchema.optional(),
    locked: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'Nothing to change' })
export type FirePatrolShiftPatch = z.infer<typeof FirePatrolShiftPatchSchema>

export const FirePatrolWeekSchema = z.object({
  dispatcherUserId: z.string().trim().min(1).max(20).nullable(),
})

export const FirePatrolCrewSchema = z.object({
  canPic: z.boolean(),
  canMc: z.boolean(),
  canDispatch: z.boolean(),
})
export type FirePatrolCrewInput = z.infer<typeof FirePatrolCrewSchema>

// What the dispatchers have cleared a person to do.
export interface FirePatrolCapabilities {
  canPic: boolean
  canMc: boolean
}

export function canTakeRole(crew: FirePatrolCapabilities | null | undefined, role: FirePatrolRole): boolean {
  if (!crew) return false
  return role === 'pic' ? crew.canPic : crew.canMc
}

export type LockBlocker = 'no_role' | 'not_cleared' | 'role_taken'

// Why a shift cannot be locked, or null when it can. `chosenRoles` are the
// roles already locked for someone else that day: a day has one chosen PIC
// and one chosen MC.
export function lockBlocker(
  shift: { role: FirePatrolRole | null },
  crew: FirePatrolCapabilities | null | undefined,
  chosenRoles: readonly FirePatrolRole[],
): LockBlocker | null {
  if (!shift.role) return 'no_role'
  if (!canTakeRole(crew, shift.role)) return 'not_cleared'
  if (chosenRoles.includes(shift.role)) return 'role_taken'
  return null
}

// The Monday of the week a YYYY-MM-DD date falls in (weeks run Monday to
// Sunday, as the calendar shows them).
export function weekStartOf(date: string): string {
  const [y, m, d] = date.split('-').map(Number) as [number, number, number]
  const isoDay = new Date(Date.UTC(y, m - 1, d)).getUTCDay() || 7
  return addDays(date, 1 - isoDay)
}

// The seven dates of the week starting on `weekStart`.
export function weekDays(weekStart: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
}

// Whole days from `from` to `to`, inclusive of both ends; NaN for a bad date.
export function rangeDays(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000) + 1
}

// An observer account, created by an administrator: someone outside the club
// (typically an MC) who needs to see the palolento calendar. They are
// invited by email to set a password, like any member.
export const CreateObserverSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().toLowerCase().email().max(254),
  phone: z
    .string()
    .trim()
    .max(40)
    .nullish()
    .transform((v) => v || null),
  locale: z.enum(['fi', 'sv', 'en']).default('fi'),
})
export type CreateObserverInput = z.infer<typeof CreateObserverSchema>
